
import React, { Component, ErrorInfo, ReactNode } from "react";
import { supabase } from '@/integrations/supabase/client';
import { clearAuthStorage } from "@/utils/authCleanup";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { 
      hasError: true, 
      error,
      errorInfo: null
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    this.setState({ errorInfo });
    
    // Check if this is an auth-related error
    const errorMsg = error.message.toLowerCase();
    const isAuthError = errorMsg.includes('auth') || 
                       errorMsg.includes('token') || 
                       errorMsg.includes('session') ||
                       errorMsg.includes('supabase');
                       
    if (isAuthError) {
      console.warn('Auth-related error caught by ErrorBoundary, clearing auth storage');
      clearAuthStorage();
    }
  }
  
  private handleReset = () => {
    // Clear all app storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Try clearing IndexedDB databases
    if (window.indexedDB) {
      window.indexedDB.databases().then(databases => {
        if (databases && databases.length > 0) {
          console.log(`Clearing ${databases.length} IndexedDB databases`);
          databases.forEach(db => {
            if (db.name) {
              try {
                window.indexedDB.deleteDatabase(db.name);
              } catch (dbError) {
                console.error(`Error deleting database ${db.name}:`, dbError);
              }
            }
          });
        }
      }).catch(error => {
        console.error('Error listing IndexedDB databases:', error);
      });
    }
    
    // Reload the page
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
          <div className="bg-destructive/10 border border-destructive/20 p-6 rounded-lg max-w-2xl w-full">
            <h2 className="text-2xl font-bold text-destructive mb-4">Something went wrong</h2>
            <p className="mb-4 text-muted-foreground">The application encountered an error.</p>
            {this.state.error && (
              <div className="bg-card p-4 rounded overflow-auto max-h-[300px] mb-4">
                <pre className="text-sm text-card-foreground">{this.state.error.toString()}</pre>
              </div>
            )}
            <div className="flex gap-4">
              <button
                onClick={() => window.location.reload()}
                className="bg-primary text-primary-foreground px-4 py-2 rounded hover:bg-primary/90 transition-colors"
              >
                Reload Page
              </button>
              <button
                onClick={this.handleReset}
                className="bg-destructive text-destructive-foreground px-4 py-2 rounded hover:bg-destructive/90 transition-colors"
              >
                Clear Cache & Reload
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
